import React, { useCallback, useMemo, useState } from 'react';

import { Button } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faFileExport, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { formatDate, SHORT_DATE_FORMAT } from 'helpers/formatters/date_time_formats';
import Paginator from '../../paginator';
import EmptyState from '../../empty_state';
import type { MailersExport } from 'bundles/connect/types';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { downloadMailersExport } from 'bundles/connect/helpers/prospect_api';

type MailerExportProps = {
  mailerExports: MailersExport[];
}

const PER_PAGE = 5;
export default function MailerExportList({ mailerExports }: MailerExportProps) {
  const [page, setPage] = useState(1);

  const paginatedResults = useMemo(() => {
    return mailerExports.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  }, [mailerExports, page]);

  const link = useCallback((ex: MailersExport) => {
    if (ex.zipFileFileSize) return (
      <Button data-turbo={false} href={downloadMailersExport(ex.id)} variant="link">
        <FontAwesomeIcon icon={faFileDownload}/>
        Download
      </Button>
    );

    if (ex.exportError) return (
      <Button className="text-danger" disabled variant="link">
        Error
      </Button>
    );

    return (
      <Button className="text-muted" disabled variant="link">
        <FontAwesomeIcon icon={faSpinner} spin/>
        Exporting
      </Button>
    );
  }, []);

  return (
    <>
      {mailerExports.length > 0 ? (
        <>
          {paginatedResults.map(ex => (
            <div className="d-flex py-2 gap-2 border-bottom" key={ex.id}>
              <FontAwesomeIcon className="align-self-start py-1" icon={faEnvelope} size="lg" />
              <div className="flex-grow-1">
                <div className="fw-bold">{ex.name}</div>
                <div className="text-muted small">
                  Exported on {formatDate(ex.createdAt, SHORT_DATE_FORMAT)}
                  {ex.lastDownloaded && (
                    <span> - Last downloaded {formatDate(ex.lastDownloaded, SHORT_DATE_FORMAT)}</span>
                  )}
                </div>
              </div>
              <div className="text-end">
                {link(ex)}
              </div>
            </div>
          
          ))}
          {mailerExports.length > PER_PAGE && (
            <div className="d-flex justify-content-center">
              <Paginator currentPage={page} perPage={PER_PAGE} total={mailerExports.length} onSelect={setPage}/>
            </div>
          )}
        </>
      ) : (
        <EmptyState icon={faFileExport} title="You don't have any mailer exports"/>
      )}
    </>
  );
}
